import { create } from 'zustand'

/**
 * Workspace tabs: every page the user opens stays open as a tab (like the old VSoft page strip), so switching
 * back finds it exactly as it was left. The router location is always the front tab's URL; this store holds the
 * rest. Pure state only: the shell (WorkspaceController) turns an active-tab change into a navigation and a
 * navigation into an active-tab update.
 */

export const TAB_LIMIT = 12
/**
 * A phone keeps few pages alive: every open tab stays mounted (keep-alive), and twelve of them made a phone hang on
 * the next page it opened (owner, 2026-10-17: "لما اختار حاجه من سايد بار … بيعلق ومش بتفتح", with 12 tabs open).
 */
export const PHONE_TAB_LIMIT = 4
export const tabLimit = () => (typeof window !== 'undefined' && window.matchMedia?.('(max-width: 767px)').matches ? PHONE_TAB_LIMIT : TAB_LIMIT)
export const TABS_STORAGE_KEY = 'aos-tabs'
export const HOME_URL = '/'

export interface TabLocation {
  pathname: string
  search: string
  hash: string
  state: unknown
  key: string
}

export interface WorkspaceTab {
  id: string
  /** pathname + search + hash, normalised (percent-encoded the way the browser reports it) */
  url: string
  pinned: boolean
  /** logical clock of the last time the tab was in front (least recently used = smallest) */
  lastUsed: number
  /** the location this tab's page renders with (keeps router `state` for the front tab) */
  loc: TabLocation
}

interface TabsData {
  tabs: WorkspaceTab[]
  activeId: string
}

interface TabsState extends TabsData {
  /** Focus the tab with this exact URL, or create one. `background` leaves the current tab in front. */
  open: (url: string, opts?: { background?: boolean }) => string
  focus: (id: string) => void
  close: (id: string) => void
  closeOthers: (id: string) => void
  closeAfter: (id: string) => void
  duplicate: (id: string) => void
  togglePin: (id: string) => void
  /** Move a tab to a new index (pinned tabs stay before the others). */
  move: (id: string, toIndex: number) => void
  cycle: (step: 1 | -1) => void
  /**
   * The router moved (a page called navigate(), browser back/forward, a link inside a page): the front tab now
   * shows that URL — unless it is a back/forward to a URL another tab already shows, which just focuses that tab.
   * `initial` (first sync after a reload) never overwrites a restored tab: it focuses or adds one.
   */
  syncLocation: (loc: Omit<TabLocation, 'key'> & { key?: string }, opts?: { pop?: boolean; initial?: boolean }) => void
  reset: () => void
}

let clock = 0
const tick = () => ++clock
let seq = 0
const newId = () => `t${Date.now().toString(36)}${(++seq).toString(36)}`

/** Canonical form so "/a?x=ب" from a link and "/a?x=%D8%A8" from the address bar are the same tab. */
export function normalizeUrl(url: string): string {
  try {
    const u = new URL(url, 'http://aos.local')
    return u.pathname + u.search + u.hash
  } catch {
    return url
  }
}

export function locationOf(url: string, state: unknown = null, key = 'default'): TabLocation {
  const u = new URL(normalizeUrl(url), 'http://aos.local')
  return { pathname: u.pathname, search: u.search, hash: u.hash, state, key }
}

export const urlOfLocation = (l: { pathname: string; search: string; hash: string }) => normalizeUrl(l.pathname + l.search + l.hash)

/** Login and the dev design previews live outside the workspace. */
export const isWorkspaceUrl = (url: string) => url.startsWith('/') && !/^\/(login|__preview)(\/|\?|#|$)/.test(url)

function makeTab(url: string, pinned = false, state: unknown = null): WorkspaceTab {
  const n = normalizeUrl(url)
  return { id: newId(), url: n, pinned, lastUsed: tick(), loc: locationOf(n, state) }
}

const homeTab = () => ({ ...makeTab(HOME_URL, true), id: 'home' })

/** Pinned tabs first, keeping each group's relative order. */
const ordered = (tabs: WorkspaceTab[]) => [...tabs.filter((t) => t.pinned), ...tabs.filter((t) => !t.pinned)]

/** Drop least-recently-used unpinned tabs (never `keep`) until there is room for `incoming` more. */
function evict(tabs: WorkspaceTab[], keep: string[], incoming = 1): WorkspaceTab[] {
  let out = tabs
  const limit = tabLimit()
  while (out.length + incoming > limit) {
    const victim = out.filter((t) => !t.pinned && !keep.includes(t.id)).sort((a, b) => a.lastUsed - b.lastUsed)[0]
    if (!victim) break
    out = out.filter((t) => t.id !== victim.id)
  }
  return out
}

const touch = (tabs: WorkspaceTab[], id: string) => tabs.map((t) => (t.id === id ? { ...t, lastUsed: tick() } : t))

/** After removing tabs, the neighbour of the old position takes the front (the next one, else the previous). */
function neighbour(before: WorkspaceTab[], after: WorkspaceTab[], closedId: string): string {
  const i = before.findIndex((t) => t.id === closedId)
  for (let d = i + 1; d < before.length; d++) if (after.some((t) => t.id === before[d]!.id)) return before[d]!.id
  for (let d = i - 1; d >= 0; d--) if (after.some((t) => t.id === before[d]!.id)) return before[d]!.id
  return after[0]?.id ?? ''
}

function ensureOne(data: TabsData): TabsData {
  if (data.tabs.length) return data.tabs.some((t) => t.id === data.activeId) ? data : { ...data, activeId: data.tabs[0]!.id }
  const home = homeTab()
  return { tabs: [home], activeId: home.id }
}

function load(): TabsData {
  try {
    const raw = localStorage.getItem(TABS_STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as { tabs?: { id?: string; url?: string; pinned?: boolean }[]; activeId?: string }
      const seen = new Set<string>()
      const tabs = (parsed.tabs ?? [])
        .filter((t) => typeof t.url === 'string' && isWorkspaceUrl(t.url) && typeof t.id === 'string' && !seen.has(t.id) && (seen.add(t.id), true))
        .slice(0, tabLimit())
        .map((t) => ({ ...makeTab(t.url!, Boolean(t.pinned)), id: t.id! }))
      if (tabs.length) return ensureOne({ tabs: ordered(tabs), activeId: parsed.activeId ?? tabs[0]!.id })
    }
  } catch {
    /* private window / blocked storage / bad JSON: start fresh */
  }
  return ensureOne({ tabs: [], activeId: '' })
}

function save({ tabs, activeId }: TabsData) {
  try {
    localStorage.setItem(TABS_STORAGE_KEY, JSON.stringify({ tabs: tabs.map(({ id, url, pinned }) => ({ id, url, pinned })), activeId }))
  } catch {
    /* storage full or blocked: tabs just won't survive a reload */
  }
}

export const useTabs = create<TabsState>()((set, get) => {
  /** Apply a change, keep the invariants (≥1 tab, pinned first, active exists) and persist. */
  const commit = (next: TabsData) => {
    const data = ensureOne({ tabs: ordered(next.tabs), activeId: next.activeId })
    set(data)
    save(data)
  }

  return {
    ...load(),

    open: (url, opts) => {
      const n = normalizeUrl(url)
      const { tabs, activeId } = get()
      const existing = tabs.find((t) => t.url === n)
      if (existing) {
        if (!opts?.background) commit({ tabs: touch(tabs, existing.id), activeId: existing.id })
        return existing.id
      }
      const tab = makeTab(n)
      if (opts?.background) tab.lastUsed = 0 // opened but never looked at: first in line if the strip overflows
      const kept = evict(tabs, [activeId])
      // a background tab goes right after the current one (browser-like); a focused one at the end
      const at = opts?.background ? kept.findIndex((t) => t.id === activeId) + 1 : kept.length
      const list = [...kept.slice(0, at), tab, ...kept.slice(at)]
      commit({ tabs: list, activeId: opts?.background ? activeId : tab.id })
      return tab.id
    },

    focus: (id) => {
      const { tabs } = get()
      if (!tabs.some((t) => t.id === id)) return
      commit({ tabs: touch(tabs, id), activeId: id })
    },

    close: (id) => {
      const { tabs, activeId } = get()
      if (!tabs.some((t) => t.id === id)) return
      const rest = tabs.filter((t) => t.id !== id)
      const nextActive = id === activeId ? neighbour(tabs, rest, id) : activeId
      commit({ tabs: nextActive ? touch(rest, nextActive) : rest, activeId: nextActive })
    },

    closeOthers: (id) => {
      const { tabs } = get()
      commit({ tabs: touch(tabs.filter((t) => t.id === id || t.pinned), id), activeId: id })
    },

    closeAfter: (id) => {
      const { tabs, activeId } = get()
      const i = tabs.findIndex((t) => t.id === id)
      if (i < 0) return
      const rest = tabs.filter((t, j) => j <= i || t.pinned)
      commit({ tabs: rest, activeId: rest.some((t) => t.id === activeId) ? activeId : id })
    },

    duplicate: (id) => {
      const { tabs } = get()
      const src = tabs.find((t) => t.id === id)
      if (!src) return
      const copy = makeTab(src.url, false, src.loc.state)
      const kept = evict(tabs, [id])
      const at = kept.findIndex((t) => t.id === id) + 1
      commit({ tabs: [...kept.slice(0, at), copy, ...kept.slice(at)], activeId: copy.id })
    },

    togglePin: (id) => {
      const { tabs, activeId } = get()
      const t = tabs.find((x) => x.id === id)
      if (!t) return
      const updated = { ...t, pinned: !t.pinned }
      const rest = tabs.filter((x) => x.id !== id)
      // pinning puts it at the end of the pinned group, unpinning at the start of the others
      const pinnedCount = rest.filter((x) => x.pinned).length
      commit({ tabs: [...rest.slice(0, pinnedCount), updated, ...rest.slice(pinnedCount)], activeId })
    },

    move: (id, toIndex) => {
      const { tabs, activeId } = get()
      const from = tabs.findIndex((t) => t.id === id)
      if (from < 0) return
      const tab = tabs[from]!
      const rest = tabs.filter((t) => t.id !== id)
      const pinnedCount = rest.filter((t) => t.pinned).length
      // a tab can only move inside its own group
      const lo = tab.pinned ? 0 : pinnedCount
      const hi = tab.pinned ? pinnedCount : rest.length
      const at = Math.max(lo, Math.min(hi, toIndex))
      if (at === from) return
      commit({ tabs: [...rest.slice(0, at), tab, ...rest.slice(at)], activeId })
    },

    cycle: (step) => {
      const { tabs, activeId } = get()
      if (tabs.length < 2) return
      const i = tabs.findIndex((t) => t.id === activeId)
      const next = tabs[(i + step + tabs.length) % tabs.length]!
      commit({ tabs: touch(tabs, next.id), activeId: next.id })
    },

    syncLocation: (loc, opts) => {
      const url = urlOfLocation(loc)
      if (!isWorkspaceUrl(url)) return
      const { tabs, activeId } = get()
      const active = tabs.find((t) => t.id === activeId)
      const full: TabLocation = { ...loc, key: loc.key ?? 'default' }
      if (active && active.url === url) {
        // same page: keep the location object (no re-render) unless navigate() carried new router state
        if (active.loc.state !== full.state) set({ tabs: tabs.map((t) => (t.id === active.id ? { ...t, loc: full } : t)) })
        return
      }
      if (opts?.pop || opts?.initial) {
        const other = tabs.find((t) => t.url === url)
        if (other) return commit({ tabs: touch(tabs, other.id), activeId: other.id })
      }
      if (opts?.initial || !active) {
        const tab = { ...makeTab(url, false, full.state), loc: full }
        return commit({ tabs: [...evict(tabs, []), tab], activeId: tab.id })
      }
      commit({ tabs: tabs.map((t) => (t.id === active.id ? { ...t, url, loc: full, lastUsed: tick() } : t)), activeId })
    },

    reset: () => {
      const home = homeTab()
      commit({ tabs: [home], activeId: home.id })
    },
  }
})

/** Re-read localStorage (tests, or another window changed the saved tabs). */
export function reloadTabsFromStorage() {
  useTabs.setState(load())
}
